<template>
  <div class="card validation-card">

    <div class="card-head d-flex justify-content-between align-items-center">
      <div>
        <h4 class="title">Validation des dossiers</h4>
        <p class="subtitle">Supervision et contrôle des validations agents</p>
      </div>

      <div class="filters">
        <div class="btn-group me-2">
          <button
            class="btn btn-sm"
            :class="viewMode === 'PENDING' ? 'btn-primary' : 'btn-outline-primary'"
            @click="() => { viewMode = 'PENDING'; loadValidations(); }"
          >
            En attente
          </button>
          <button
            class="btn btn-sm"
            :class="viewMode === 'PROCESSED' ? 'btn-primary' : 'btn-outline-primary'"
            @click="() => { viewMode = 'PROCESSED'; loadValidations(); }"
          >
            Traités
          </button>
        </div>

        <Multiselect
          class="filter-select"
          v-model="selectedAgent"
          :options="agentOptions"
          placeholder="Agent"
          @change="() => loadValidations()"
        />

        <Multiselect
          class="filter-select"
          v-model="selectedQueue"
          :options="queueOptions"
          placeholder="File"
          @change="() => loadValidations()"
        />

        <button class="btn btn-outline-primary btn-sm" @click="() => loadValidations()">
          <i class="flaticon-refresh"></i>
        </button>
      </div>
    </div>

    <div class="card-body">

      <div class="row g-3 mb-4">
        <div class="col-md-3" v-for="stat in statsUI" :key="stat.label">
          <div class="stat-card">
            <div class="stat-icon" :class="stat.type">
              <i :class="stat.icon"></i>
            </div>
            <div>
              <div class="stat-value">{{ stat.value }}</div>
              <div class="stat-label">{{ stat.label }}</div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="viewMode === 'PENDING' && selectedValidations.length"
        class="bulk-bar"
      >
        <span>
          <i class="flaticon-info"></i>
          {{ selectedValidations.length }} dossier(s) sélectionné(s)
        </span>
        <div>
          <button class="btn btn-success btn-sm" @click="openModalBulk('APPROVED')">
            <i class="flaticon-check"></i> Approuver
          </button>
          <button class="btn btn-warning btn-sm" @click="openModalBulk('REJECTED')">
            <i class="flaticon-close"></i> Rejeter
          </button>
          <button class="btn btn-light btn-sm" @click="clearSelection">
            Annuler
          </button>
        </div>
      </div>

      <div class="table-responsive">
        <table class="table text-nowrap align-middle mb-0">
          <thead>
            <tr>
              <!-- Checkbox uniquement en attente -->
              <th v-if="viewMode === 'PENDING'" scope="col" 
                class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                <input
                  type="checkbox"
                  class="form-check-input"
                  @change="toggleSelectAll"
                  :checked="isAllSelected"
                />
              </th>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">BESC</th>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">Agent</th>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">File</th>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">Terminé</th>
              <!-- Statut toujours visible -->
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">Statut</th>
              <!-- Actions uniquement en attente -->
              <th v-if="viewMode === 'PENDING'" scope="col" 
                class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0 text-end pe-0">
                Actions
              </th>
            </tr>
          </thead>

          <tbody>
            <tr v-if="!validations.length">
              <td :colspan="viewMode === 'PENDING' ? 7 : 5" class="text-center text-muted py-4">
                <i class="flaticon-folder-open"></i>
                <p>Aucune validation trouvée</p>
              </td>
            </tr>

            <tr v-for="v in validations" :key="v.id">
              <!-- Checkbox -->
              <td v-if="viewMode === 'PENDING'">
                <input
                  type="checkbox"
                  class="form-check-input"
                  v-model="selectedValidations"
                  :value="v.id"
                />
              </td>

              <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                <strong>{{ v.refbesc }}</strong>
              </td>

              <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                <div v-if="v.assignedAgent">
                  {{ v.assignedAgent.nom }} {{ v.assignedAgent.prenom }}
                  <div class="muted">{{ v.assignedAgent.email }}</div>
                </div>
                <span v-else class="muted">—</span>
              </td>

              <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                <div v-if="v.queue">
                  {{ v.queue.name }}
                  <div class="muted">{{ v.queue.code }}</div>
                </div>
                <span v-else class="muted">—</span>
              </td>

              <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                {{ viewMode === 'PENDING' 
                  ? format_date(v.completedAt) 
                  : format_date(v.supervisorValidatedAt) }}
              </td>

              <!-- <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                {{ v.waitingTime || '—' }}
              </td> -->

              <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                <span class="badge" :class="statusClass(v.supervisorDecision)">
                  {{ statusLabel(v.supervisorDecision) }}
                </span>
              </td>

              <td v-if="viewMode === 'PENDING'" 
                class="shadow-none lh-1 fw-medium text-body-tertiary text-end pe-0">
                <div class="dropdown">
                  <span 
                    class="badge text-white bg-primary fs-15 dropdown-toggle"
                    data-bs-toggle="dropdown" 
                    aria-expanded="false"
                    style="cursor: pointer;">
                    Actions
                    <i class="flaticon-chevron-2 position-relative ms-5 top-2 fs-15"></i>
                  </span>
                  <ul class="dropdown-menu dropdown-menu-end">
                  <li>
                    <a
                      class="dropdown-item d-flex align-items-center"
                      href="#"
                      @click.prevent="openModalSingle(v, 'APPROVED')"
                    >
                      <i class="flaticon-check lh-1 me-8 position-relative top-1"></i>
                      Approuver
                    </a>
                  </li>
                  <li>
                    <a
                      class="dropdown-item d-flex align-items-center"
                      href="#"
                      @click.prevent="openModalSingle(v, 'REJECTED')"
                    >
                      <i class="flaticon-close lh-1 me-8 position-relative top-1"></i>
                      Rejeter
                    </a>
                  </li>
                </ul>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="pagination-area d-md-flex mt-15 mt-sm-20 mt-md-25 justify-content-between align-items-center">
        <PaginationComponent
          :page="page"
          :totalPages="totalPages"
          :totalElements="totalElements"
          :limit="limit"
          @paginate="handlePaginate"
        />
      </div>
    </div>

    <!-- MODAL CONFIRMATION -->
    <div class="modal fade" id="validationModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              {{ modalData.decision === 'APPROVED' ? 'Approuver' : 'Rejeter' }}
              {{ modalData.isBulk ? 'les dossiers' : 'le dossier' }}
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>

          <div class="modal-body">
            <div v-if="modalData.isBulk" class="alert alert-info">
              Vous êtes sur le point de {{ modalData.decision === 'APPROVED' ? 'approuver' : 'rejeter' }}
              <strong>{{ selectedValidations.length }}</strong> dossier(s).
            </div>
            <div v-else class="alert alert-info">
              Dossier : <strong>{{ modalData.validation?.refbesc }}</strong>
            </div>

            <div class="mb-3">
              <label class="form-label">
                Commentaire
                <span v-if="modalData.decision === 'REJECTED'" class="text-danger">*</span>
              </label>
              <textarea
                v-model="modalData.comment"
                class="form-control"
                rows="4"
                :placeholder="modalData.decision === 'APPROVED' ? 'Commentaire optionnel...' : 'Veuillez préciser la raison du rejet...'"
              ></textarea>
            </div>
          </div>

          <div class="modal-footer">
            <button type="button" class="btn btn-light" data-bs-dismiss="modal">Annuler</button>
            <button
              type="button"
              class="btn"
              :class="modalData.decision === 'APPROVED' ? 'btn-success' : 'btn-warning'"
              @click="confirmValidation"
              :disabled="modalData.decision === 'REJECTED' && !modalData.comment.trim()"
            >
              {{ modalData.decision === 'APPROVED' ? 'Approuver' : 'Rejeter' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>


<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue';
import ApiService from '@/services/ApiService';
import PaginationComponent from '@/components/Utilities/Pagination.vue';
import Multiselect from '@vueform/multiselect';
import { Modal } from 'bootstrap';
import { format_date, monSwal, success, error } from '@/utils/utils';


interface Agent {
  id: string;
  nom: string;
  prenom: string;
}

interface Queue {
  id: string;
  name: string;
  code: string;
}
interface ValidationItem {
  id: string;
  refbesc: string;
  completedAt: string | null;
  supervisorValidatedAt: string | null; // ✅ Ajouter
  waitingTime: string | null;
  supervisorDecision: 'APPROVED' | 'REJECTED' | null;
  assignedAgent?: {
    id: string;
    nom: string;
    prenom: string;
    email?: string | null;
  };
  queue?: {
    id: string;
    name: string;
    code: string;
  };
}



export default defineComponent({
  name: 'SuperviseurValidation',
  components: { PaginationComponent, Multiselect },

  setup() {
  

    const validations = ref<ValidationItem[]>([]);
    const agents = ref<Agent[]>([]);
    const queues = ref<Queue[]>([]);

    const selectedValidations = ref<string[]>([]);
    const selectedAgent = ref('');
    const selectedQueue = ref('');

    const viewMode = ref<'PENDING' | 'PROCESSED'>('PENDING');

    const page = ref(1);
    const limit = ref(10);
    const totalPages = ref(0);
    const totalElements = ref(0);


    const modalData = ref({
      decision: '' as 'APPROVED' | 'REJECTED',
      comment: '',
      isBulk: false,
      validation: null as ValidationItem | null
    });

    let modalInstance: Modal | null = null;

    

    const agentOptions = computed(() => [
      { value: '', label: 'Tous les agents' },
      ...agents.value.map(a => ({
        value: a.id,
        label: `${a.nom} ${a.prenom}`
      }))
    ]);

    const queueOptions = computed(() => [
      { value: '', label: 'Toutes les files' },
      ...queues.value.map(q => ({
        value: q.id,
        label: q.name
      }))
    ]);

    const statsUI = computed(() => {
      const vals = validations.value;

      if (viewMode.value === 'PROCESSED') {
        return [
          {
            label: 'Approuvés',
            value: vals.filter(v => v.supervisorDecision === 'APPROVED').length,
            icon: 'flaticon-check',
            type: 'success'
          },
          {
            label: 'Rejetés',
            value: vals.filter(v => v.supervisorDecision === 'REJECTED').length,
            icon: 'flaticon-close',
            type: 'danger'
          },
          {
            label: 'Total',
            value: vals.length,
            icon: 'flaticon-folder',
            type: 'primary'
          }
        ];
      }

      return [
        {
          label: 'En attente',
          value: vals.filter(v => !v.supervisorDecision).length,
          icon: 'flaticon-time',
          type: 'warning'
        },
        {
          label: 'Total',
          value: vals.length,
          icon: 'flaticon-folder',
          type: 'primary'
        }
      ];
    });

    const isAllSelected = computed(() => {
      const pendingIds = validations.value
        .filter(v => !v.supervisorDecision)
        .map(v => v.id);

      return (
        pendingIds.length > 0 &&
        pendingIds.every(id => selectedValidations.value.includes(id))
      );
    });

   

    const loadValidations = async (p = page.value, l = limit.value) => {
      page.value = p;
      limit.value = l;

      const params = new URLSearchParams({
        page: String(p),
        limit: String(l)
      });

      if (selectedAgent.value) params.append('agentId', selectedAgent.value);
      if (selectedQueue.value) params.append('queueId', selectedQueue.value);

      const endpoint =
        viewMode.value === 'PENDING'
          ? '/supervisor/validations'
          : '/supervisor/approuve';

      const res = await ApiService.get(`${endpoint}?${params}`);

      validations.value = res.data.data.data || [];
      totalPages.value = res.data.data.totalPages || 0;
      totalElements.value = res.data.data.totalElements || 0;
    };

    const loadFilters = async () => {
      agents.value =
        (await ApiService.get('/users?role=Agent&limit=0')).data.data.data || [];

      queues.value =
        (await ApiService.get('/validation-queues?limit=0')).data.data.data || [];
    };

    
    const openModalSingle = (
      validation: ValidationItem,
      decision: 'APPROVED' | 'REJECTED'
    ) => {
      modalData.value = {
        decision,
        comment:
          decision === 'REJECTED'
            ? 'Rejeté par le superviseur'
            : 'Approuvé par le superviseur',
        isBulk: false,
        validation
      };

      modalInstance ??= new Modal(
        document.getElementById('validationModal')!
      );
      modalInstance.show();
    };

    const openModalBulk = (decision: 'APPROVED' | 'REJECTED') => {
      modalData.value = {
        decision,
        comment:
          decision === 'REJECTED'
            ? 'Rejeté par le superviseur'
            : 'Approuvé par le superviseur',
        isBulk: true,
        validation: null
      };

      modalInstance ??= new Modal(
        document.getElementById('validationModal')!
      );
      modalInstance.show();
    };

    const executeValidateSingle = async (validation: ValidationItem) => {
      try {
        await ApiService.post(
          `/supervisor/validate-dossier/${validation.id}`,
          {
            decision: modalData.value.decision,
            supervisorComment: modalData.value.comment
          }
        );

        success(
          modalData.value.decision === 'APPROVED'
            ? 'Dossier approuvé avec succès'
            : 'Dossier rejeté avec succès'
        );

        await loadValidations();
      } catch (err: any) {
        error(err.response?.data?.message || 'Une erreur est survenue');
      }
    };

    const executeBulkValidate = async () => {
      try {
        await ApiService.post('/supervisor/bulk-validate', {
          validationIds: selectedValidations.value,
          decision: modalData.value.decision,
          supervisorComment: modalData.value.comment
        });

        success(
          modalData.value.decision === 'APPROVED'
            ? `${selectedValidations.value.length} dossier(s) approuvé(s) avec succès`
            : `${selectedValidations.value.length} dossier(s) rejeté(s) avec succès`
        );

        selectedValidations.value = [];
        await loadValidations();
      } catch (err: any) {
        error(err.response?.data?.message || 'Une erreur est survenue');
      }
    };

    const confirmValidation = async () => {
      const actionText = modalData.value.decision === 'APPROVED' ? 'approuver' : 'rejeter';
      const targetText = modalData.value.isBulk
        ? `${selectedValidations.value.length} dossier(s)`
        : `le dossier ${modalData.value.validation?.refbesc}`;

      const confirmed = await monSwal(
        `Vous êtes sur le point de ${actionText} ${targetText}. Voulez-vous continuer ?`,
        'Confirmer',
        modalData.value.decision === 'APPROVED' ? 'btn btn-success' : 'btn btn-warning',
        'Annuler',
        'Opération annulée'
      );

      if (!confirmed) {
        return;
      }

      modalData.value.isBulk
        ? await executeBulkValidate()
        : modalData.value.validation &&
          await executeValidateSingle(modalData.value.validation);

      modalInstance?.hide();
    };

    
    const toggleSelectAll = () => {
      const pending = validations.value.filter(v => !v.supervisorDecision);
      selectedValidations.value = isAllSelected.value
        ? []
        : pending.map(v => v.id);
    };

    const statusClass = (decision: ValidationItem['supervisorDecision']) => {
      if (decision === 'APPROVED') return 'success';
      if (decision === 'REJECTED') return 'danger';
      return 'pending';
    };

    const statusLabel = (decision: ValidationItem['supervisorDecision']) => {
      if (decision === 'APPROVED') return 'Approuvé';
      if (decision === 'REJECTED') return 'Rejeté';
      return 'En attente';
    };


    onMounted(async () => {
      await loadFilters();
      await loadValidations();
    });


    return {
      validations,
      selectedValidations,
      selectedAgent,
      selectedQueue,
      agentOptions,
      queueOptions,
      statsUI,
      page,
      limit,
      totalPages,
      totalElements,
      isAllSelected,
      modalData,
      viewMode,
      loadValidations,
      openModalSingle,
      openModalBulk,
      confirmValidation,
      toggleSelectAll,
      format_date,
      statusClass,
      statusLabel,
      clearSelection: () => (selectedValidations.value = []),
      handlePaginate: ({ page_, limit_ }: any) =>
        loadValidations(page_, limit_)
    };
  }
});
</script>

<style scoped>
.validation-card {
  border: 1px solid #eef0f4;
  border-radius: 12px;
}

.card-head {
  padding: 16px 20px;
  border-bottom: 1px solid #eef0f4;
}

.title {
  margin: 0;
  font-weight: 600;
}

.subtitle {
  font-size: 13px;
  color: #6c757d;
}

.filters {
  display: flex;
  gap: 10px;
  align-items: center;
}

.filter-select {
  width: 180px;
  font-size: 13px;
}

.filter-select.small {
  width: 140px;
}

/* Stats */
.stat-card {
  background: #fff;
  border: 1px solid #eef0f4;
  border-radius: 10px;
  padding: 14px;
  display: flex;
  gap: 12px;
}

.stat-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-icon.success {
  background: #eafaf1;
  color: #198754;
}

.stat-icon.warning {
  background: #fff7e6;
  color: #f0ad4e;
}

.stat-icon.danger {
  background: #fdecea;
  color: #dc3545;
}

.stat-icon.primary {
  background: #eef4ff;
  color: #0d6efd;
}

.stat-value {
  font-size: 20px;
  font-weight: 600;
}

.stat-label {
  font-size: 12px;
  color: #6c757d;
}

/* Table */
.validation-table th {
  font-size: 12px;
  color: #6c757d;
}

.validation-table td {
  font-size: 13px;
  vertical-align: middle;
}

.empty {
  text-align: center;
  color: #adb5bd;
  padding: 40px;
}

.agent {
  display: flex;
  gap: 10px;
}

.avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #f1f3f5;
  display: flex;
  align-items: center;
  justify-content: center;
}

.muted {
  font-size: 12px;
  color: #6c757d;
}

/* Status */
.status {
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
}

.status.pending {
  background: #fff7e6;
  color: #f0ad4e;
}

.status.success {
  background: #eafaf1;
  color: #198754;
}

.status.danger {
  background: #fdecea;
  color: #dc3545;
}

.icon-btn {
  background: transparent;
  border: none;
}

/* Bulk Bar */
.bulk-bar {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.bulk-bar span {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #495057;
}

.bulk-bar button {
  margin-left: 8px;
}
</style>